import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { withLatestFrom, switchMap, map, first } from 'rxjs/operators';
import { AuthenticationService, isNotNullOrUndefined, StateService, System } from 'ngx-core-services';
import { Action, Store } from '@ngrx/store';
import { of, from } from 'rxjs';

import { SystemDefinition } from '../state/system.state';
import {
  checkSystemMetadataSuccess,
  checkSystemMetadataFailed,
  addSystem,
  updateSystem,
  checkSystemMetadata,
  changeMetadataLoadingState,
} from '../actions/system.actions';
import { AppState, isSystemEqualToSystemDefinition, updateConnextData } from '..';
import { checkAndUpdateSystemDefinitionIfNecessary } from '../reducers/system.reducer';
import {
  checkAndUpdateSystemLocation,
  checkAndUpdateSystemMeterSensor,
  checkAndUpdateSystemStructure,
  checkAndUpdateAuthorizationInPlant,
  checkAndUpdateSystemTz,
  checkAndUpdateConnectionSystemStatus,
} from './metadata';
import { selectAwsConfigBySystemId } from '../selectors/system.selectors';
import { updateAzzurroHubData } from '../actions/azzurroHub.actions';

@Injectable()
export class SystemMetadataEffects {

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private authenticationService: AuthenticationService,
    private readonly stateService: StateService,
  ) {}

  checkIfCalcMetadataIsNeeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateConnextData, updateAzzurroHubData, updateSystem, addSystem),
      withLatestFrom(this.store),
      switchMap((input) => {
        /**
         * if I don't have Connext data, I wait for them to arrive
         */
        const [action, { systemState }] = input;
        const system = systemState.systemList[action.systemId];
        const { monitoringType, data } = system;

        const waitingForConnextData = monitoringType === 'Connext' && !data;
        if (waitingForConnextData) {
          return this.actions$.pipe(
            ofType(updateConnextData),
            first(),
            withLatestFrom(this.store)
          );
        }

        const waitingForAzzurroHubData = monitoringType === 'AzzurroHub' && !data?.info;
        if (waitingForAzzurroHubData) {
          return this.actions$.pipe(
            ofType(updateAzzurroHubData),
            first(),
            withLatestFrom(this.store)
          );
        }

        const waitingForAwsData = system.source === 'aws' && !system.aws?.config;
        if (waitingForAwsData) {
          return this.store.select(selectAwsConfigBySystemId(action.systemId)).pipe(
            isNotNullOrUndefined(),
            first(),
            withLatestFrom(this.store),
            map(() => input),
          );
        }

        return of(input);
      }),
      map(([action, { systemState }]) => {
        const system = systemState.systemList[action.systemId];
        if (!system) {
          return null;
        }

        if (system.isMetadataLoading) {
          return null;
        }

        return checkSystemMetadata({ systemId: action.systemId });
      }),
      isNotNullOrUndefined(),
    ),
  );

  calculateSystemMetadata$ = createEffect(() =>
    this.actions$.pipe(
      ofType(checkSystemMetadata),
      withLatestFrom(this.store),
      switchMap(([action, { systemState }]) => {
        const system = systemState.systemList[action.systemId];
        if (!system) {
          return of(null);
        }
        this.store.dispatch(changeMetadataLoadingState({ systemId: system.uid!, loading: true }));
        return from(this.checkAndUpdateSystem({ ...system }));
      }),
      isNotNullOrUndefined(),
    ),
  );

  async checkAndUpdateSystem(system: SystemDefinition): Promise<Action> {
    try {
      const myEmail = this.authenticationService.user$.value?.email!;

      let changes = await checkAndUpdateSystemTz({ ...system });
      changes = checkAndUpdateSystemStructure(changes);
      changes = checkAndUpdateSystemMeterSensor(changes);
      changes = checkAndUpdateConnectionSystemStatus(changes);
      changes = checkAndUpdateSystemLocation(changes);
      changes = checkAndUpdateAuthorizationInPlant(changes, myEmail);

      /** Save to Redux store */
      checkAndUpdateSystemDefinitionIfNecessary(system, changes, this.store);

      /** Get the updated system */
      await this.updateSystemInFirebaseIfChanged(changes);

      return checkSystemMetadataSuccess({ systemId: system.uid!, comment: 'System metadata updated successfully.' });

    } catch {
      return checkSystemMetadataFailed({ systemId: system.uid!, error: 'Unknown error occurred while checking and updating system.' });
    } finally {
      this.store.dispatch(changeMetadataLoadingState({ systemId: system.uid!, loading: false }));
    }
  }

  async updateSystemInFirebaseIfChanged(changeDef: SystemDefinition) {
    if (changeDef.source !== 'firebase' || !changeDef.uid) {
      return;
    }

    const currentSystem = this.stateService.getCurrentSystem(changeDef.uid);

    if (!currentSystem) {
      return;
    }

    if (!isSystemEqualToSystemDefinition(currentSystem, changeDef)) {
      const updatedSystem: System = {
        uid: changeDef.uid,
        connextSn: changeDef.monitoringType === 'Connext' ? (changeDef.connextSn ?? changeDef.data?.info?.sn?.sn ?? '') : '',
        configuration: changeDef.configuration,
        devices: changeDef.devices,
        img: changeDef.img,
        name: changeDef.name,
        source: 'firebase',
      };

      await this.stateService.saveCurrentSystem(updatedSystem);
    }
  }
}
